import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';
import SuccessMetricCard from './success-metric-card';

/** The metric types available in the type picker. */
const METRIC_TYPE_OPTIONS = [
  { label: 'Email Open Rate', value: 'email_opened' },
  { label: 'Email Response Rate', value: 'email_replied' },
  { label: 'Email Click Rate', value: 'email_clicked' },
  { label: 'Interaction Created', value: 'interaction_created' },
  { label: 'Gift Given', value: 'gift_given' },
  { label: 'Portfolio Stage Reached', value: 'portfolio_stage_reached' },
  { label: 'Saved Search Qualification', value: 'saved_search' },
];

/** Creates a new metric with defaults. */
const createEmptyMetric = (type) => ({
  id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  type,
  targetPercentage: type === 'email_opened' || type === 'email_replied' ? 50 : 20,
  isPrimary: true,
  config: {},
});

const isDebugMode = () => {
  try {
    return new URLSearchParams(window.location.search).get('debug') === 'true';
  } catch {
    return false;
  }
};

/**
 * Success Metrics section.
 * Normal mode: single optional success metric.
 * Debug mode (?debug=true): allows multiple success metrics.
 */
const SuccessMetricsSection = ({
  metric,
  metrics: metricsProp,
  onChange,
  onChangeMultiple,
  interactionTypes = [],
  savedSearches = [],
  portfolioTeams = {},
}) => {
  const [showTypePicker, setShowTypePicker] = useState(false);
  const pickerRef = useRef(null);
  const debugMode = isDebugMode();

  // Multi-metric state for debug mode
  const metrics = metricsProp || (metric ? [metric] : []);

  // Close type picker on outside click
  useEffect(() => {
    if (!showTypePicker) return;
    const handleClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowTypePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showTypePicker]);

  const handleSelectMetric = (type) => {
    const newMetric = createEmptyMetric(type);
    if (debugMode && onChangeMultiple) {
      onChangeMultiple([...metrics, newMetric]);
    } else {
      onChange(newMetric);
    }
    setShowTypePicker(false);
  };

  const handleUpdateMetric = (index, updated) => {
    if (debugMode && onChangeMultiple) {
      const next = [...metrics];
      next[index] = updated;
      onChangeMultiple(next);
    } else {
      onChange(updated);
    }
  };

  const handleRemoveMetric = (index) => {
    if (debugMode && onChangeMultiple) {
      const next = metrics.filter((_, i) => i !== index);
      onChangeMultiple(next);
    } else {
      onChange(null);
    }
  };

  const canAddMore = debugMode || metrics.length === 0;
  const usedTypes = new Set(metrics.map((m) => m.type));
  const availableTypes = METRIC_TYPE_OPTIONS.filter((opt) => !usedTypes.has(opt.value));

  return (
    <div className="success-metrics-section">
      {metrics.length > 0 && (
        <div className="success-metrics-section--cards">
          {metrics.map((m, i) => (
            <SuccessMetricCard
              key={m.id || i}
              metric={m}
              onChange={(updated) => handleUpdateMetric(i, updated)}
              onRemove={() => handleRemoveMetric(i)}
              interactionTypes={interactionTypes}
              savedSearches={savedSearches}
              portfolioTeams={portfolioTeams}
            />
          ))}
        </div>
      )}

      {canAddMore && availableTypes.length > 0 && (
        <div className="success-metrics-section--add-wrapper" ref={pickerRef}>
          <button
            type="button"
            className="success-metrics-section--add-btn"
            onClick={() => setShowTypePicker(!showTypePicker)}
          >
            <Icon icon="add" /> Add a success metric
          </button>
          {showTypePicker && (
            <div className="success-metrics-section--type-picker">
              {availableTypes.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className="success-metrics-section--type-option"
                  onClick={() => handleSelectMetric(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {debugMode && metrics.length > 0 && (
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
          Debug mode: {metrics.length} metric{metrics.length !== 1 ? 's' : ''} configured
        </div>
      )}
    </div>
  );
};

SuccessMetricsSection.propTypes = {
  metric: PropTypes.object,
  metrics: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onChangeMultiple: PropTypes.func,
  interactionTypes: PropTypes.array,
  savedSearches: PropTypes.array,
  portfolioTeams: PropTypes.object,
};

export default SuccessMetricsSection;
